import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  buildCoordinateReferenceSystemPayload,
  defaultCoordinateSystemPreference,
  getCoordinateSystemMode,
  parseCoordinateSystemPreference,
  type CoordinateSystemPreference,
} from 'utils/coordinateSystemManager';
import { firstPolygon, normalizeMultiPolygon } from 'utils/multiPolygonHelper';

export type GridParams = {
  legalBorder: Array<any>;
  legalBorders: Array<Array<any>>;
  buildableBorder: Array<any>;
  buildableBorders: Array<Array<any>>;
  analysisArea: Array<any>;
  analysisAreas: Array<Array<any>>;
  activeAnalysisAreaIndex: number;
  analysisAreaType: string;
  analysisAreaName: string;
  coordinateReferenceSystem: {
    rotation: number;
    point: {
      x: number;
      y: number;
      z: number;
    };
  };
  manualCenter: {
    x: number;
    y: number;
  } | null;
  gridResolution: number | string;
  gridAngle: number;
  gridHeight: number;
  customGridElevationMode: string | null;
  customGridElevation: number;
  isFlatTop: boolean;
  sensorPointsHeightFromGround: number;
  terrainType: string;
  terrainResolutionClose: number;
  flatTerrainMode: string | null;
  flatTerrainHeight: number;
  openVisualizerBtn: boolean;
  fileForUploadedTerrain: any;
  fileForUploadedScene: any;
  fileForTrees: any;
  [key: string]: any;
};

type GridState = {
  name: string;
  description: string;
  createdAt?: string | number | Date | null;
  createdBy?: string | null;
  params: GridParams;
};

type SensorPointsState = {
  totalSensorPoints: number | null;
  message: string;
  type: string;
  showBillingLink?: boolean;
};

type CloneGridOptions = {
  appendClonedSuffix?: boolean;
};

type GridContextValue = {
  isCloningGrid: boolean;
  setIsCloningGrid: Dispatch<SetStateAction<boolean>>;
  grid: GridState;
  gridParams: GridParams;
  coordinateSystemPreference: CoordinateSystemPreference;
  setCoordinateSystemPreference: (nextPreference: Partial<CoordinateSystemPreference>) => void;
  setGridParams: (newParams: Partial<GridParams>) => void;
  setGrid: Dispatch<SetStateAction<GridState>>;
  clearGridFormData: () => void;
  cloneGridData: (pItem?: any, options?: CloneGridOptions) => void;
  sensorPoints: SensorPointsState;
  setSensorPoints: Dispatch<SetStateAction<SensorPointsState>>;
  sensorPointLimit: number | null;
  setSensorPointLimit: Dispatch<SetStateAction<number | null>>;
  volumeLimit: number | null;
  setVolumeLimit: Dispatch<SetStateAction<number | null>>;
  gridVolume: number | null;
  setGridVolume: Dispatch<SetStateAction<number | null>>;
};

export const GridContext = createContext<GridContextValue>({} as GridContextValue);

const gridData: GridState = {
  name: '',
  description: '',
  createdAt: null,
  createdBy: null,
  params: {
    legalBorder: [],
    legalBorders: [],
    buildableBorder: [],
    buildableBorders: [],
    analysisArea: [],
    analysisAreas: [],
    activeAnalysisAreaIndex: 0,
    analysisAreaType: '',
    analysisAreaName: '',
    coordinateReferenceSystem: {
      rotation: 0,
      point: {
        x: 0,
        y: 0,
        z: 0,
      },
    },
    manualCenter: null,
    gridResolution: 1.0,
    gridAngle: 0.0,
    gridHeight: 9.0,
    customGridElevationMode: null,
    customGridElevation: 1.0,
    isFlatTop: false,
    sensorPointsHeightFromGround: 1.5,
    terrainType: 'TERRAIN_RESOLUTION',
    terrainResolutionClose: 0.5,
    flatTerrainMode: null,
    flatTerrainHeight: 0,
    openVisualizerBtn: false,
    fileForUploadedTerrain: null,
    fileForUploadedScene: null,
    fileForTrees: null,
  },
};

type GridContextProviderProps = {
  children: ReactNode;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

export const areGridParamValuesEqual = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => areGridParamValuesEqual(item, right[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    return (
      leftKeys.length === rightKeys.length && leftKeys.every((key) => areGridParamValuesEqual(left[key], right[key]))
    );
  }

  return false;
};

const GridContextProvider = ({ children }: GridContextProviderProps) => {
  const [isCloningGrid, setIsCloningGrid] = useState(false);
  const [grid, setGrid] = useState<GridState>(gridData);
  const initialSensorPoints: SensorPointsState = {
    totalSensorPoints: null,
    message: '',
    type: '',
    showBillingLink: false,
  };
  const [sensorPoints, setSensorPoints] = useState(initialSensorPoints);
  const [sensorPointLimit, setSensorPointLimit] = useState<number | null>(null);
  const [volumeLimit, setVolumeLimit] = useState<number | null>(null);
  const [gridVolume, setGridVolume] = useState<number | null>(null);
  const [coordinateSystemUiMode, setCoordinateSystemUiMode] = useState(defaultCoordinateSystemPreference.mode);
  const derivedCoordinateSystemPreference = useMemo(
    () => parseCoordinateSystemPreference(grid?.params),
    [grid?.params]
  );
  const coordinateSystemPreference = useMemo(
    () => ({
      ...derivedCoordinateSystemPreference,
      mode: coordinateSystemUiMode,
    }),
    [derivedCoordinateSystemPreference, coordinateSystemUiMode]
  );

  const clearGridFormData = () => {
    setGrid(gridData);
    setGridVolume(null);
    setSensorPoints(initialSensorPoints);
    setCoordinateSystemUiMode(defaultCoordinateSystemPreference.mode);
  };

  const setGridParams = (newParams: Partial<GridParams>) => {
    setGrid((prevState) => {
      const hasRealChange = Object.entries(newParams).some(([key, value]) => {
        return !areGridParamValuesEqual(prevState.params[key], value);
      });

      if (!hasRealChange) {
        return prevState;
      }

      return {
        ...prevState,
        params: {
          ...prevState.params,
          ...newParams,
        },
      };
    });
  };

  const setCoordinateSystemPreference = useCallback((nextPreference: Partial<CoordinateSystemPreference>) => {
    setGrid((prevState) => {
      const derivedPreference = parseCoordinateSystemPreference(prevState?.params);
      const mergedPreference =
        nextPreference?.mode === 'lv95'
          ? {
              ...defaultCoordinateSystemPreference,
              mode: 'lv95' as const,
            }
          : {
              ...derivedPreference,
              ...nextPreference,
            };
      const nextUiMode = nextPreference?.mode ?? getCoordinateSystemMode(mergedPreference);
      setCoordinateSystemUiMode(nextUiMode);

      return {
        ...prevState,
        params: {
          ...prevState.params,
          ...buildCoordinateReferenceSystemPayload(mergedPreference),
        },
      };
    });
  }, []);

  const cloneGridData = useCallback((pItem: any = {}, options: CloneGridOptions = {}) => {
    const { appendClonedSuffix = true } = options;
    const params = pItem?.params ?? pItem ?? {};
    const resolveStoredFile = (baseKey: string) => {
      if (appendClonedSuffix) {
        return null;
      }

      const importedFileByField: Record<string, unknown> = {
        fileForUploadedScene: params?.customSceneFile,
        fileForTrees: params?.treesFile,
      };

      return params?.[baseKey] || importedFileByField[baseKey] || null;
    };
    const safeParse = (value: unknown) => {
      if (Array.isArray(value)) {
        return value;
      }

      try {
        return JSON.parse(String(value || '[]'));
      } catch {
        return [];
      }
    };
    const parseNumber = (value: unknown, fallback: number) => {
      const parsed = parseFloat(String(value ?? fallback));
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const coordinateSystemPreference = parseCoordinateSystemPreference(params);
    const clonedGridData: GridState = {
      name: pItem?.name ? `${pItem.name}${appendClonedSuffix ? ' cloned' : ''}` : '',
      description: pItem?.description || '',
      createdAt: pItem?.createdAt ?? null,
      createdBy: pItem?.createdBy ?? null,
      params: {
        legalBorder: firstPolygon(safeParse(params?.legalBorders ?? params?.legalBorder)),
        legalBorders: normalizeMultiPolygon(safeParse(params?.legalBorders ?? params?.legalBorder)),
        buildableBorder: firstPolygon(safeParse(params?.buildableBorders ?? params?.buildableBorder)),
        buildableBorders: normalizeMultiPolygon(safeParse(params?.buildableBorders ?? params?.buildableBorder)),
        analysisArea: firstPolygon(safeParse(params?.analysisAreas ?? params?.analysisArea)),
        analysisAreas: normalizeMultiPolygon(safeParse(params?.analysisAreas ?? params?.analysisArea)),
        activeAnalysisAreaIndex: parseNumber(params?.activeAnalysisAreaIndex, 0),
        analysisAreaType: params?.analysisAreaType || '',
        analysisAreaName: params?.analysisAreaName || '',
        ...buildCoordinateReferenceSystemPayload(coordinateSystemPreference),
        manualCenter: params?.manualCenter
          ? { x: parseNumber(params?.manualCenter?.x, 0.0), y: parseNumber(params?.manualCenter?.y, 0.0) }
          : null,
        gridResolution: parseNumber(params?.gridResolution, 1.0),
        gridAngle: parseNumber(params?.gridAngle, 0.0),
        gridHeight: parseNumber(params?.gridHeight, 9.0),
        customGridElevationMode:
          params?.customGridElevationMode || (params?.customGridElevation != null ? 'Absolute' : null),
        customGridElevation: parseNumber(params?.customGridElevation, 1.0),
        isFlatTop: Boolean(params?.isFlatTop || false),
        sensorPointsHeightFromGround: parseNumber(params?.sensorPointsHeightFromGround, 1.5),
        terrainType: params?.terrainType || 'TERRAIN_RESOLUTION',
        terrainResolutionClose: parseNumber(params?.terrainResolutionClose, 0.5),
        flatTerrainHeight: parseNumber(params?.flatTerrainHeight, 0),
        flatTerrainMode: params?.flatTerrainMode || (parseNumber(params?.flatTerrainHeight, 0) > 0 ? 'Absolute' : null),
        openVisualizerBtn: false,
        fileForUploadedTerrain: resolveStoredFile('fileForUploadedTerrain'),
        fileForUploadedScene: resolveStoredFile('fileForUploadedScene'),
        fileForTrees: resolveStoredFile('fileForTrees'),
      },
    };

    setGrid(clonedGridData);
    setSensorPoints(initialSensorPoints);
    setCoordinateSystemUiMode(coordinateSystemPreference.mode);
  }, []);

  return (
    <GridContext.Provider
      value={{
        isCloningGrid,
        setIsCloningGrid,
        grid,
        gridParams: grid?.params,
        coordinateSystemPreference,
        setCoordinateSystemPreference,
        setGridParams,
        setGrid,
        clearGridFormData,
        cloneGridData,
        sensorPoints,
        setSensorPoints,
        sensorPointLimit,
        setSensorPointLimit,
        volumeLimit,
        setVolumeLimit,
        gridVolume,
        setGridVolume,
      }}
    >
      {children}
    </GridContext.Provider>
  );
};

export default GridContextProvider;
